<template>
  <!-- Footer -->
  <footer class="bg-gray-900 text-white py-12 w-full">
    <div class="px-4">
      <div class="grid md:grid-cols-4 gap-8">
        <div class="animate__animated animate__fadeInUp">
          <h3 class="text-xl font-bold mb-4">TREVIA SYSTEMS &amp; SERVICES</h3>
          <p class="mb-4">Delivering efficient and resilient solutions that prioritize affordability while ensuring 100% customer satisfaction.</p>
          <div class="flex space-x-4">
            <a
                v-for="(social, index) in socialLinks"
                :key="index"
                :href="social.href"
                class="text-gray-400 hover:text-yellow-500 transition duration-300"
            >
              <svg
                  class="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
              >
                <path :d="social.path"></path>
              </svg>
            </a>
          </div>
        </div>
        <div class="animate__animated animate__fadeInUp animate__delay-1s">
          <h3 class="text-xl font-bold mb-4">Quick Links</h3>
          <ul class="space-y-2">
            <li v-for="link in navLinks" :key="link.to">
              <router-link :to="link.to" class="hover:text-yellow-500">{{ link.text }}</router-link>
            </li>
          </ul>
        </div>
        <div class="animate__animated animate__fadeInUp animate__delay-2s">
          <h3 class="text-xl font-bold mb-4">Services</h3>
          <ul class="space-y-2">
            <li v-for="service in services" :key="service.link">
              <router-link :to="service.link" class="hover:text-yellow-500">{{ service.title }}</router-link>
            </li>
          </ul>
        </div>
        <div class="animate__animated animate__fadeInUp animate__delay-3s">
          <h3 class="text-xl font-bold mb-4">Contact Us</h3>
          <address class="not-italic">
            <p class="mb-2">Addis Ababa Rd, Industrial Area</p>
            <p class="mb-2">P.O Box 310 – 00510, Nairobi, Kenya</p>
            <p class="mb-2">+254 707 034 507</p>
            <p class="mb-4">info@tss.co.ke</p>
            <router-link
                to="/contact"
                class="bg-yellow-500 text-blue-900 px-4 py-2 rounded font-bold hover:bg-yellow-600 inline-block transition duration-300 animate__animated animate__pulse animate__infinite"
            >
              Get in Touch
            </router-link>
          </address>
        </div>
      </div>
      <div class="border-t border-gray-800 mt-8 pt-8 text-center animate__animated animate__fadeIn">
        <p>&copy; {{ currentYear }} Trevia Systems &amp; Services Limited. All rights reserved.</p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';

// Dynamic copyright year
const currentYear = computed(() => new Date().getFullYear());

const navLinks = [
  { to: '/', text: 'Home' },
  { to: '/about', text: 'About Us' },
  { to: '/services', text: 'Services' },
  { to: '/clients', text: 'Clients' },
  { to: '/contact', text: 'Contact' },
];

const services = [
  {
    icon: '⚡',
    title: 'General Electrical Installations',
    description: 'Design, installation, maintenance, and repair of electrical equipment for all sectors.',
    link: '/services/electrical',
  },
  {
    icon: '🔋',
    title: 'Power Generation & Backup',
    description: 'Standby generators, UPS systems, voltage regulators, and surge protection.',
    link: '/services/power',
  },
  {
    icon: '💻',
    title: 'Data Center Power Systems',
    description: 'Critical power solutions for telecom, banking, and data centers.',
    link: '/services/datacenter',
  },
  {
    icon: '🏠',
    title: 'Home & Commercial Automation',
    description: 'IoT solutions for lighting, climate control, security, and energy management.',
    link: '/services/automation',
  },
  {
    icon: '🔥',
    title: 'Fire Safety & Protection',
    description: 'Fire detection, alarm systems, suppression systems, and compliance services.',
    link: '/services/fire-safety',
  },
];

// Social Links
const socialLinks = [
  {
    href: '#',
    path: 'M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z',
  },
  {
    href: '#',
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  },
  {
    href: '#',
    path: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z',
  },
];
</script>